/**
 * QuizQuestion
 *
 * A component for displaying interactive quiz questions in chat messages
 *
 * Primary responsibilities:
 * - Display a question with multiple choice options (A, B, C, D format)
 * - Allow selection of an answer
 * - Show feedback and explanation for correct/incorrect answers
 * - Provide in-depth "Help Me Understand" section for deeper learning
 * - Track and display quiz scores and performance
 * - Support completing quiz sessions with summary feedback
 */

import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  IconQuestionMark,
  IconCheck,
  IconX,
  IconArrowsShuffle,
  IconBulb,
  IconSchool,
  IconTrophy,
  IconChartBar,
  IconCircleCheck
} from '@tabler/icons-react'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import { motion, AnimatePresence } from 'framer-motion'

interface QuizQuestionProps {
  question: string
  options: string[]
  correctAnswer: string // The letter (A, B, C, D) of the correct answer
  explanation: string
  conceptExplanation?: string // Deeper conceptual explanation
  feedback?: string // Personalized feedback for the student based on their answer
  categoryTag?: string // Optional tag for categorization like "JavaScript", "React", "CSS"
  difficultyLevel?: 'easy' | 'medium' | 'hard' // Optional difficulty level
  onScoreUpdate?: (result: { correct: boolean; category?: string; difficulty?: string }) => void // Optional callback for score tracking
  onQuizComplete?: (summary: {
    totalQuestions: number
    correctAnswers: number
    score: number
  }) => void // Callback when quiz is completed
  isLastQuestion?: boolean // Flag to indicate if this is the last question in a quiz sequence
  className?: string
}

// Create a global state to track quiz performance across sessions
const globalQuizStats = {
  totalAttempted: 0,
  totalCorrect: 0,
  categoriesAttempted: new Map<string, { correct: number; total: number }>(),
  difficultiesAttempted: new Map<string, { correct: number; total: number }>(),
  currentQuizQuestions: 0,
  currentQuizCorrect: 0,
  quizCompleted: false
}

export const QuizQuestion: React.FC<QuizQuestionProps> = ({
  question,
  options,
  correctAnswer,
  explanation,
  conceptExplanation,
  feedback,
  categoryTag,
  difficultyLevel,
  onScoreUpdate,
  onQuizComplete,
  isLastQuestion = false,
  className
}) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const [showExplanation, setShowExplanation] = useState(false)
  const [showConceptHelp, setShowConceptHelp] = useState(false)
  const [showStats, setShowStats] = useState(false)
  const [quizCompleted, setQuizCompleted] = useState(false)

  // Get the quiz difficulty level with a default
  const difficulty = difficultyLevel || 'medium'

  // Create display text for difficulty
  const difficultyDisplay = {
    easy: 'Easy',
    medium: 'Medium',
    hard: 'Advanced'
  }

  // Reset quiz tracking when component is first mounted
  useEffect(() => {
    if (!globalQuizStats.quizCompleted) {
      // Only increment question count when first mounting
      globalQuizStats.currentQuizQuestions += 1
    }

    // Clean up on unmount
    return () => {
      // Reset the quiz stats if the component is unmounted and this is the last question
      if (isLastQuestion) {
        globalQuizStats.currentQuizQuestions = 0
        globalQuizStats.currentQuizCorrect = 0
        globalQuizStats.quizCompleted = false
      }
    }
  }, [isLastQuestion])

  const handleOptionSelect = (value: string) => {
    setSelectedOption(value)
    setShowExplanation(true)

    // Track score when an answer is selected
    const isCorrect = value === correctAnswer

    // Update global stats
    globalQuizStats.totalAttempted++
    if (isCorrect) {
      globalQuizStats.totalCorrect++
      globalQuizStats.currentQuizCorrect++
    }

    // Update category stats if provided
    if (categoryTag) {
      const categoryStats = globalQuizStats.categoriesAttempted.get(categoryTag) || {
        correct: 0,
        total: 0
      }
      categoryStats.total++
      if (isCorrect) categoryStats.correct++
      globalQuizStats.categoriesAttempted.set(categoryTag, categoryStats)
    }

    // Update difficulty stats
    if (difficulty) {
      const difficultyStats = globalQuizStats.difficultiesAttempted.get(difficulty) || {
        correct: 0,
        total: 0
      }
      difficultyStats.total++
      if (isCorrect) difficultyStats.correct++
      globalQuizStats.difficultiesAttempted.set(difficulty, difficultyStats)
    }

    // Call the callback if provided
    if (onScoreUpdate) {
      onScoreUpdate({
        correct: isCorrect,
        category: categoryTag,
        difficulty
      })
    }
  }

  const resetQuestion = () => {
    setSelectedOption(null)
    setShowExplanation(false)
    setShowConceptHelp(false)
    setShowStats(false)
  }

  const toggleConceptHelp = () => {
    setShowConceptHelp(!showConceptHelp)
  }

  const toggleStats = () => {
    setShowStats(!showStats)
  }

  const completeQuiz = () => {
    // Mark the quiz as completed
    setQuizCompleted(true)
    globalQuizStats.quizCompleted = true

    // Calculate score percentage
    const scorePercentage = Math.round(
      (globalQuizStats.currentQuizCorrect / globalQuizStats.currentQuizQuestions) * 100
    )

    // Call the onQuizComplete callback with summary information
    if (onQuizComplete) {
      onQuizComplete({
        totalQuestions: globalQuizStats.currentQuizQuestions,
        correctAnswers: globalQuizStats.currentQuizCorrect,
        score: scorePercentage
      })
    }
  }

  const isCorrect = selectedOption !== null && selectedOption === correctAnswer
  const isIncorrect = selectedOption !== null && selectedOption !== correctAnswer

  // Split explanation into basic and concept parts if not provided separately
  const hasExplicitConceptExplanation = !!conceptExplanation
  const parsedExplanation =
    !hasExplicitConceptExplanation && explanation
      ? explanation.split('[UNDERSTAND]').map((part) => part.trim())
      : [explanation, conceptExplanation || '']

  const basicExplanation = parsedExplanation[0]
  const derivedConceptExplanation = parsedExplanation.length > 1 ? parsedExplanation[1] : ''
  const finalConceptExplanation = hasExplicitConceptExplanation
    ? conceptExplanation
    : derivedConceptExplanation

  // Create option letters (A, B, C, D, etc.)
  const optionLetters = options.map(
    (_, index) => String.fromCharCode(65 + index) // 65 is ASCII for 'A'
  )

  // Calculate the current score and percentage
  const currentCorrectPercentage =
    globalQuizStats.totalAttempted > 0
      ? Math.round((globalQuizStats.totalCorrect / globalQuizStats.totalAttempted) * 100)
      : 0

  // Calculate category score if available
  const categoryScore = categoryTag && globalQuizStats.categoriesAttempted.get(categoryTag)
  const categoryPercentage =
    categoryScore && categoryScore.total > 0
      ? Math.round((categoryScore.correct / categoryScore.total) * 100)
      : 0

  // Calculate difficulty score
  const difficultyScore = difficulty && globalQuizStats.difficultiesAttempted.get(difficulty)
  const difficultyPercentage =
    difficultyScore && difficultyScore.total > 0
      ? Math.round((difficultyScore.correct / difficultyScore.total) * 100)
      : 0

  // Calculate current quiz score
  const currentQuizPercentage =
    globalQuizStats.currentQuizQuestions > 0
      ? Math.round(
          (globalQuizStats.currentQuizCorrect / globalQuizStats.currentQuizQuestions) * 100
        )
      : 0

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 25,
        duration: 0.4
      }}
      className={cn(
        'my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        isCorrect
          ? 'border-green-400 bg-green-50 dark:bg-green-900/10'
          : isIncorrect
            ? 'border-red-400 bg-red-50 dark:bg-red-900/10'
            : 'border-border bg-card',
        className
      )}
    >
      <motion.div
        className="flex items-center border-b p-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.3 }}
      >
        <div className="flex flex-wrap items-center gap-2">
          <IconQuestionMark size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">Quiz Question</h3>
          {categoryTag && (
            <motion.span
              className="ml-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.3 }}
            >
              {categoryTag}
            </motion.span>
          )}
          {difficulty && (
            <motion.span
              className={cn(
                'ml-1 rounded-full px-2 py-0.5 text-xs font-medium',
                difficulty === 'easy'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                  : difficulty === 'medium'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
              )}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.25, duration: 0.3 }}
            >
              {difficultyDisplay[difficulty]}
            </motion.span>
          )}
          <AnimatePresence>
            {isCorrect && (
              <motion.span
                className="ml-2 inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/20 dark:text-green-300"
                initial={{ opacity: 0, scale: 0.5, x: -20 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.5, x: -20 }}
                transition={{ type: 'spring', stiffness: 500, damping: 25 }}
              >
                <IconCheck size={12} className="mr-1" />
                Correct
              </motion.span>
            )}
            {isIncorrect && (
              <motion.span
                className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/20 dark:text-red-300"
                initial={{ opacity: 0, scale: 0.5, x: -20 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.5, x: -20 }}
                transition={{ type: 'spring', stiffness: 500, damping: 25 }}
              >
                <IconX size={12} className="mr-1" />
                Incorrect
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      <div className="flex flex-col gap-4 p-4">
        <p className="text-sm font-medium">{question}</p>

        <RadioGroup
          value={selectedOption || ''}
          onValueChange={handleOptionSelect}
          className="space-y-2"
        >
          {options.map((option, index) => {
            const letter = optionLetters[index]
            return (
              <motion.div
                key={index}
                className={cn(
                  'flex items-start space-x-2 rounded-md p-2 transition-colors',
                  selectedOption === letter &&
                    letter === correctAnswer &&
                    'bg-green-100 dark:bg-green-900/20',
                  selectedOption === letter &&
                    letter !== correctAnswer &&
                    'bg-red-100 dark:bg-red-900/20',
                  selectedOption !== null &&
                    letter === correctAnswer &&
                    'bg-green-50 dark:bg-green-900/10'
                )}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  delay: 0.3 + index * 0.1,
                  duration: 0.3,
                  ease: 'easeOut'
                }}
                whileHover={{
                  scale: selectedOption === null ? 1.02 : 1,
                  backgroundColor: selectedOption === null ? 'rgba(0,0,0,0.02)' : undefined,
                  transition: { duration: 0.15 }
                }}
              >
                <RadioGroupItem
                  value={letter}
                  id={`option-${letter}`}
                  disabled={selectedOption !== null}
                  className="mt-0.5"
                />
                <Label
                  htmlFor={`option-${letter}`}
                  className={cn(
                    'flex-1 cursor-pointer text-sm leading-relaxed',
                    selectedOption !== null && 'cursor-default'
                  )}
                >
                  <span className="mr-2 font-medium">{letter}.</span> {option}
                  <AnimatePresence>
                    {selectedOption !== null && letter === correctAnswer && (
                      <motion.div
                        className="ml-2 inline-block"
                        initial={{ opacity: 0, scale: 0.5 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.5 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 25 }}
                      >
                        <IconCheck className="inline size-4 text-green-600 dark:text-green-400" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Label>
              </motion.div>
            )
          })}
        </RadioGroup>

        {showExplanation && basicExplanation && (
          <div className="mt-2 rounded-md bg-blue-50 p-3 dark:bg-blue-900/10">
            <div className="mb-1 flex items-center gap-2">
              <IconBulb size={16} className="text-blue-600 dark:text-blue-400" />
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300">Explanation</h4>
            </div>
            <p className="whitespace-pre-wrap text-sm text-blue-700 dark:text-blue-200">
              {basicExplanation}
            </p>
          </div>
        )}

        {isIncorrect && feedback && (
          <div className="mt-2 rounded-md border border-amber-100 bg-amber-50 p-3 dark:border-amber-800/30 dark:bg-amber-900/10">
            <div className="mb-2 flex items-center gap-2">
              <IconBulb size={16} className="text-amber-600 dark:text-amber-400" />
              <h4 className="text-sm font-medium text-amber-800 dark:text-amber-300">
                Personalized Feedback
              </h4>
            </div>
            <p className="whitespace-pre-wrap text-sm text-amber-700 dark:text-amber-200">
              {feedback}
            </p>
          </div>
        )}

        {showConceptHelp && finalConceptExplanation && (
          <div className="mt-2 rounded-md border border-purple-100 bg-purple-50 p-3 dark:border-purple-800/30 dark:bg-purple-900/10">
            <div className="mb-2 flex items-center gap-2">
              <IconSchool size={16} className="text-purple-600 dark:text-purple-400" />
              <h4 className="text-sm font-medium text-purple-800 dark:text-purple-300">
                Help Me Understand
              </h4>
            </div>
            <p className="whitespace-pre-wrap text-sm text-purple-700 dark:text-purple-200">
              {finalConceptExplanation}
            </p>
          </div>
        )}
        {showStats && selectedOption !== null && (
          <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/10">
            <div className="mb-2 flex items-center gap-2">
              <IconTrophy size={16} className="text-amber-500" />
              <h4 className="text-sm font-medium">Your Performance</h4>
            </div>

            <div className="space-y-3">
              {globalQuizStats.currentQuizQuestions > 0 && (
                <div>
                  <div className="text-muted-foreground mb-1 flex justify-between text-xs">
                    <span>Current Quiz</span>
                    <span>
                      {globalQuizStats.currentQuizCorrect} / {globalQuizStats.currentQuizQuestions}{' '}
                      ({currentQuizPercentage}%)
                    </span>
                  </div>
                  <Progress
                    value={currentQuizPercentage}
                    className="h-2 bg-lime-200 dark:bg-lime-900"
                  />
                </div>
              )}

              <div>
                <div className="text-muted-foreground mb-1 flex justify-between text-xs">
                  <span>Overall</span>
                  <span>
                    {globalQuizStats.totalCorrect} / {globalQuizStats.totalAttempted} (
                    {currentCorrectPercentage}%)
                  </span>
                </div>
                <Progress value={currentCorrectPercentage} className="h-2" />
              </div>

              {categoryTag && categoryScore && (
                <div>
                  <div className="text-muted-foreground mb-1 flex justify-between text-xs">
                    <span>{categoryTag}</span>
                    <span>
                      {categoryScore.correct} / {categoryScore.total} ({categoryPercentage}%)
                    </span>
                  </div>
                  <Progress
                    value={categoryPercentage}
                    className="h-2 bg-blue-200 dark:bg-blue-900"
                  />
                </div>
              )}

              {difficulty && difficultyScore && (
                <div>
                  <div className="text-muted-foreground mb-1 flex justify-between text-xs">
                    <span>{difficultyDisplay[difficulty]} Questions</span>
                    <span>
                      {difficultyScore.correct} / {difficultyScore.total} ({difficultyPercentage}%)
                    </span>
                  </div>
                  <Progress
                    value={difficultyPercentage}
                    className={cn(
                      'h-2',
                      difficulty === 'easy'
                        ? 'bg-green-200 dark:bg-green-900'
                        : difficulty === 'medium'
                          ? 'bg-yellow-200 dark:bg-yellow-900'
                          : 'bg-red-200 dark:bg-red-900'
                    )}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {quizCompleted && (
          <div className="mt-4 rounded-md border border-green-100 bg-green-50 p-4 dark:border-green-800/30 dark:bg-green-900/10">
            <div className="mb-3 flex items-center gap-2">
              <IconTrophy size={20} className="text-green-600 dark:text-green-400" />
              <h3 className="text-base font-medium text-green-800 dark:text-green-300">
                Quiz Completed!
              </h3>
            </div>

            <div className="space-y-3">
              <div>
                <div className="mb-1 flex justify-between text-sm text-green-700 dark:text-green-300">
                  <span className="font-medium">Final Score:</span>
                  <span>
                    {globalQuizStats.currentQuizCorrect} / {globalQuizStats.currentQuizQuestions}{' '}
                    questions ({currentQuizPercentage}%)
                  </span>
                </div>
                <Progress
                  value={currentQuizPercentage}
                  className={cn(
                    'mb-3 h-3',
                    currentQuizPercentage > 80
                      ? 'bg-green-400 dark:bg-green-500'
                      : currentQuizPercentage > 60
                        ? 'bg-yellow-400 dark:bg-yellow-500'
                        : 'bg-red-400 dark:bg-red-500'
                  )}
                />
              </div>

              <p className="text-sm text-green-700 dark:text-green-300">
                {currentQuizPercentage >= 80
                  ? "Excellent work! You've mastered this topic."
                  : currentQuizPercentage >= 60
                    ? "Good job! You're making progress on this topic."
                    : "Keep practicing! You'll improve with more study."}
              </p>
            </div>
          </div>
        )}
        {/* Action buttons moved to bottom */}
        {selectedOption !== null && (
          <div className="mt-3 flex flex-wrap gap-2 border-t pt-3">
            <Button variant="outline" size="sm" onClick={toggleStats} className="text-xs">
              <IconChartBar size={14} className="mr-1" />
              {showStats ? 'Hide Stats' : 'Show Stats'}
            </Button>

            {finalConceptExplanation && (
              <Button variant="outline" size="sm" onClick={toggleConceptHelp} className="text-xs">
                <IconSchool size={14} className="mr-1" />
                {showConceptHelp ? 'Hide Help' : 'Help Me Understand'}
              </Button>
            )}

            {!quizCompleted && (
              <Button variant="outline" size="sm" onClick={resetQuestion} className="text-xs">
                <IconArrowsShuffle size={14} className="mr-1" />
                Try Again
              </Button>
            )}

            {!quizCompleted && isLastQuestion && (
              <Button
                variant="outline"
                size="sm"
                onClick={completeQuiz}
                className="ml-auto bg-green-50 text-xs text-green-600 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30"
              >
                <IconCircleCheck size={14} className="mr-1" />
                Complete Quiz
              </Button>
            )}
          </div>
        )}
      </div>
    </motion.div>
  )
}
